import { useState, ReactNode, useEffect, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

import { AdvisorConnectResponse, InvalidateTokenBody, ProfileResponse, useInvalidateToken } from '@/api';
import { AUTH0_LOGOUT_RETURN_TO } from '@/constants/auth0';
import { demoUser } from '@/data/demoUser';
import { getDemoMode, useDemoContext } from '@/hooks/DemoContext';

import { AuthActionsContext, AuthStateContext } from './AuthContext';

type LoginResponse =
  | (AdvisorConnectResponse &
      ProfileResponse & {
        justLoggedIn?: boolean;
      })
  | null;

type AuthActions = {
  login: (userData: LoginResponse) => void;
  logout: () => void;
  updateUser: (updatedFields: Partial<LoginResponse>) => void;
};

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { isDemo, deactivateDemo } = useDemoContext();
  const { mutate: invalidateToken } = useInvalidateToken();
  const { logout: auth0Logout } = useAuth0();

  const [user, setUser] = useState<LoginResponse>(() => {
    if (getDemoMode()) return demoUser;
    const storedUser = sessionStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const prevIsDemoRef = useRef(isDemo);
  useEffect(() => {
    if (prevIsDemoRef.current === isDemo) return;
    prevIsDemoRef.current = isDemo;
    setUser(isDemo ? demoUser : null);
  }, [isDemo]);

  const userRef = useRef(user);
  const isDemoRef = useRef(isDemo);
  const deactivateDemoRef = useRef(deactivateDemo);
  const queryClientRef = useRef(queryClient);
  const invalidateTokenRef = useRef(invalidateToken);
  const auth0LogoutRef = useRef(auth0Logout);

  userRef.current = user;
  isDemoRef.current = isDemo;
  deactivateDemoRef.current = deactivateDemo;
  queryClientRef.current = queryClient;
  invalidateTokenRef.current = invalidateToken;
  auth0LogoutRef.current = auth0Logout;

  const actionsRef = useRef<AuthActions>({
    login: (userData) => {
      if (userData) {
        const userWithFlag = { ...userData, justLoggedIn: true };
        setUser(userWithFlag);
        sessionStorage.setItem('islogin', 'true');
        sessionStorage.setItem('user', JSON.stringify(userWithFlag));
      }
    },
    logout: () => {
      // No refresh_token in the body on purpose: the API's extractor chain checks the body
      // before the cookie and stops at the first non-null value, so sending the key at all
      // (even empty) would shadow the real refresh_token cookie and never invalidate it server-side.
      // La vue conseiller et le mode démo ne passent pas par Auth0 : pas de session Auth0 à fermer
      const closeAuth0Session = !isDemoRef.current && !userRef.current?.advisorView;
      invalidateTokenRef.current(
        { data: {} as InvalidateTokenBody },
        {
          // Après l'invalidation du refresh token : la déconnexion Auth0 quitte l'application (redirection)
          onSettled: () => {
            if (closeAuth0Session) auth0LogoutRef.current({ logoutParams: { returnTo: AUTH0_LOGOUT_RETURN_TO } });
          },
        }
      );
      setUser(null);
      sessionStorage.setItem('islogin', 'false');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('parcoursVersementData');
      sessionStorage.removeItem('user');
      queryClientRef.current.clear();
      if (isDemoRef.current) deactivateDemoRef.current();
    },
    updateUser: (updatedFields) => {
      if (!userRef.current) return;
      const updatedUser = { ...userRef.current, ...updatedFields };
      setUser(updatedUser);
      if (!isDemoRef.current) sessionStorage.setItem('user', JSON.stringify(updatedUser));
    },
  });

  return (
    <AuthStateContext.Provider value={user}>
      <AuthActionsContext.Provider value={actionsRef.current}>{children}</AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};

export type { LoginResponse, AuthActions };
export { AuthProvider };
